import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Trophy, Globe, Users, Flame, Crown, Medal } from "lucide-react";
import PhoneFrame from "../components/PhoneFrame.jsx";
import BottomNav from "../components/BottomNav.jsx";
import api from "../lib/api.js";

const SCOPES = [
  { k: "friends", label: "Friends", icon: Users },
  { k: "global", label: "Global", icon: Globe },
];
const PERIODS = [
  { k: "day", label: "Today" },
  { k: "week", label: "Week" },
  { k: "month", label: "Month" },
  { k: "all", label: "All time" },
];

const TIER_COLORS = ["#FBBF24", "#cbd5e1", "#c08457"]; // gold / silver / bronze

export default function Leaderboard() {
  const nav = useNavigate();
  const [scope, setScope] = useState("friends");
  const [period, setPeriod] = useState("week");
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get(`/leaderboard?scope=${scope}&period=${period}`).then((r) => setData(r.data)).catch(() => setData({ rows: [], me: null }));
  }, [scope, period]);

  const rows = data?.rows || [];
  const podium = rows.slice(0, 3);
  const rest = rows.slice(3);
  const me = data?.me;

  return (
    <PhoneFrame>
      <div className="ps-aurora min-h-screen">
        <div className="px-5 pt-6 pb-28 relative">
          <button onClick={() => nav(-1)} className="text-slate-400 inline-flex items-center gap-1 text-sm" data-testid="leaderboard-back">
            <ChevronLeft size={16} /> Back
          </button>

          <div className="mt-5 ps-fade-up">
            <div className="ps-chip mb-3"><Trophy size={11} /> Leaderboard</div>
            <h1 className="ps-heading text-4xl font-extrabold leading-[1]">
              Climb<span className="ps-serif ps-italic font-normal text-slate-500">.</span>
            </h1>
            <p className="text-slate-400 text-sm mt-2 ps-serif ps-italic">Steps win. Show up daily.</p>
          </div>

          {/* Scope tabs */}
          <div className="mt-5 flex gap-1 bg-[#0E1F2A]/60 backdrop-blur border border-[#1B364A] rounded-full p-1">
            {SCOPES.map((s) => {
              const Ic = s.icon;
              const active = scope === s.k;
              return (
                <button
                  key={s.k}
                  data-testid={`lb-scope-${s.k}`}
                  onClick={() => setScope(s.k)}
                  className={`flex-1 py-2 rounded-full text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${active ? "bg-gradient-to-br from-[#4ade80] to-[#34d399] text-[#022014]" : "text-slate-400 hover:text-white"}`}
                >
                  <Ic size={12} /> {s.label}
                </button>
              );
            })}
          </div>

          {/* Period selector */}
          <div className="mt-3 flex gap-1.5 overflow-x-auto pb-1">
            {PERIODS.map((p) => (
              <button
                key={p.k}
                data-testid={`lb-period-${p.k}`}
                onClick={() => setPeriod(p.k)}
                className={`px-3 py-1.5 rounded-full text-[11px] font-bold whitespace-nowrap border transition-colors ${period === p.k ? "bg-[#4ade80]/15 text-[#4ade80] border-[#4ade80]/40" : "bg-[#0E1F2A]/40 text-slate-400 border-[#1B364A]"}`}
              >{p.label}</button>
            ))}
          </div>

          {!data ? (
            <div className="mt-10 text-center text-slate-400 ps-serif ps-italic">Tallying steps…</div>
          ) : rows.length === 0 ? (
            <div className="mt-10 text-center text-slate-400 ps-serif ps-italic">
              No competitors yet. {scope === "friends" && "Add friends to start climbing."}
            </div>
          ) : (
            <>
              {/* Podium */}
              <div className="mt-6 grid grid-cols-3 gap-2 items-end ps-fade-up">
                {[1, 0, 2].map((i) => {
                  const u = podium[i];
                  if (!u) return <div key={i} />;
                  const height = i === 0 ? 130 : i === 1 ? 100 : 90;
                  const color = TIER_COLORS[i];
                  return (
                    <div key={u.user_id} className="flex flex-col items-center" data-testid={`lb-podium-${u.rank}`}>
                      <div className="relative">
                        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#15303E] to-[#0E1F2A] border-2 flex items-center justify-center text-lg font-extrabold ps-heading" style={{ borderColor: color }}>
                          {u.name?.[0]?.toUpperCase() || "?"}
                        </div>
                        {i === 0 && (
                          <Crown size={16} className="absolute -top-3 left-1/2 -translate-x-1/2" color={color} fill={color} />
                        )}
                      </div>
                      <div className="mt-2 text-[11px] font-bold text-center max-w-[80px] truncate">{u.name}</div>
                      <div className="text-[10px] ps-tabular font-bold" style={{ color }}>{u.total_steps.toLocaleString()}</div>
                      <div
                        className="w-full mt-2 rounded-t-xl border-t-2 border-x relative overflow-hidden"
                        style={{ height, borderColor: `${color}aa`, background: `linear-gradient(180deg, ${color}1a 0%, transparent 100%)` }}
                      >
                        <div className="absolute inset-0 flex items-start justify-center pt-1.5">
                          <Medal size={14} color={color} />
                        </div>
                        <div className="absolute bottom-1.5 left-0 right-0 text-center ps-heading font-extrabold text-lg" style={{ color }}>
                          {u.rank}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Your rank pinned (if outside top 3) */}
              {me && me.rank > 3 && (
                <div className="mt-5 ps-card p-3 flex items-center gap-3 border-[#4ade80]/40" data-testid="lb-me-pinned" style={{ borderColor: "#4ade80aa" }}>
                  <div className="w-9 h-9 rounded-full bg-[#4ade80]/20 border border-[#4ade80]/40 flex items-center justify-center font-extrabold text-sm ps-tabular text-[#4ade80]">
                    #{me.rank}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-[#4ade80] font-bold">You</div>
                    <div className="font-bold truncate">{me.name}</div>
                  </div>
                  <div className="text-right">
                    <div className="ps-heading font-extrabold ps-tabular">{me.total_steps.toLocaleString()}</div>
                    <div className="text-[10px] text-slate-500 ps-serif ps-italic">steps</div>
                  </div>
                </div>
              )}

              {/* Rest of list */}
              <div className="mt-5 space-y-2 ps-stagger">
                {rest.map((u) => (
                  <Row key={u.user_id} u={u} />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
      <BottomNav />
    </PhoneFrame>
  );
}

function Row({ u }) {
  return (
    <div className={`ps-card p-3 flex items-center gap-3 ${u.is_me ? "border-[#4ade80]/50" : ""}`} data-testid={`lb-row-${u.rank}`}>
      <div className="w-8 text-center font-extrabold text-sm ps-tabular text-slate-400">{u.rank}</div>
      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#15303E] to-[#0E1F2A] border border-[#1B364A] flex items-center justify-center font-bold text-sm">
        {u.name?.[0]?.toUpperCase() || "?"}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold truncate text-sm flex items-center gap-1.5">
          {u.name}
          {u.is_me && <span className="text-[9px] uppercase tracking-widest text-[#4ade80] font-bold">You</span>}
        </div>
        <div className="flex items-center gap-2 text-[10px] text-slate-500">
          <span className="inline-flex items-center gap-0.5"><Flame size={9} color="#FBBF24" />{u.streak}d</span>
          <span>·</span>
          <span>{u.sessions} sessions</span>
        </div>
      </div>
      <div className="text-right">
        <div className="ps-heading font-extrabold ps-tabular text-sm">{u.total_steps.toLocaleString()}</div>
        <div className="text-[10px] text-slate-500 ps-serif ps-italic">steps</div>
      </div>
    </div>
  );
}
